import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import type { Signal } from '@angular/core';

@Component({
  selector: 'app-npt-summary-tab',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './npt-summary-tab.component.html',
  styleUrls: ['./npt-summary-tab.component.scss'],
})
export class NptSummaryTabComponent implements OnChanges {
  @Input() isMultiWellMode = false;
  @Input() totalPaDurationHrs = 0;
  @Input() paDurationDays = '0';
  @Input() multiWellTotalHrs = 0;
  @Input() nptTotalHrs: any = 0;
  @Input() nptPct: any = 0;
  @Input() multiWellTotalNptHrs = 0;
  @Input() nptEvents: any[] = [];
  @Input() multiWellNptEvents: any[] = [];
  @Input() nptCost = 0;
  @Input() multiWellTotalNptCost = 0;
  @Input() causalAnalysis: any[] = [];
  @Input() multiWellCausalAnalysis: any[] = [];
  @Input() activeWellTimeLog: any[] = [];
  @Input() multiWellSelectedNames: string[] = [];
  @Input() wellNameMap: { [key: string]: string } = {};
  @Input() wellsWithoutNptEvents: string[] = [];
  @Input() originalSelectedWellsCount = 0;
  @Input() selectedWellForTimeLog: string | null = null;
  @Input() showTimeLog = false;
  @Input() activeWellName = '';
  @Input() currentWellId = '';
  @Input() loadingTimeLog = false;
  @Input() apiTimeLogError: string | null = null;
  @Input() showDailyOps = false;
  @Input() selectedWellForDailyOps: string | null = null;
  @Input() dailyOpsWells: { id: string; name: string }[] = [];
  @Input() dailyOpsRows: { wellName: string; reportStartDate: string; summary: string }[] = [];
  @Input() dailyOpsLoading = false;
  @Input() dailyOpsError: string | null = null;
  @Input() apiRcaLoading = false;
  @Input() rcaSubmittingIndex: number | null = null;
  @Input() hasRcaAccess!: Signal<boolean>; // Access control signal for RCA table

  @Output() toggleTimeLog = new EventEmitter<void>();
  @Output() toggleDailyOps = new EventEmitter<void>();
  @Output() closeDailyOps = new EventEmitter<void>();
  @Output() selectWellForDailyOps = new EventEmitter<string>();
  @Output() closeTimeLog = new EventEmitter<void>();
  @Output() selectWellForTimeLog = new EventEmitter<string>();
  @Output() loadTimeLogRetry = new EventEmitter<string>();
  @Output() submitRcaFeedback = new EventEmitter<{ eventId: string; verification: string; wellId: string }>();
  @Output() refreshCausalAnalysis = new EventEmitter<void>();

  getTimeLogForWell(wellName: string): any[] {
    return []; // Parent provides this via activeWellTimeLog for single well
  }

  selectWellForTimeLogHandler(wellId: string): void {
    this.selectWellForTimeLog.emit(wellId);
  }

  retryLoadTimeLog(wellId: string): void {
    this.loadTimeLogRetry.emit(wellId);
  }

  rcaStatusMap: Record<number, string> = {};
  rcaSubmittedMap: Record<number, boolean> = {};
  rcaSubmittingMap: Record<number, boolean> = {};

  setRcaStatus(index: number, status: string): void {
    this.rcaStatusMap = { ...this.rcaStatusMap, [index]: status };
  }

  submitRcaStatus(index: number, row: any): void {
    const verification = this.rcaStatusMap[index] === 'Validated' ? 'approved' : 'rejected';
    // Mark this row as currently submitting
    this.rcaSubmittingMap = { ...this.rcaSubmittingMap, [index]: true };
    this.submitRcaFeedback.emit({
      eventId: row.eventId || row.nptEventId,
      verification: verification,
      wellId: row.wellId || this.currentWellId
    });
  }

  fmtK(n: number): string {
    const isNegative = n < 0;
    const formatted = '$' + Math.abs(n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return isNegative ? '-' + formatted : formatted;
  }

  fmt2Decimal(n: number): string {
    return n.toFixed(2);
  }

  fmtVariance(value: number): { text: string; color: string; arrow: string } {
    const text = value.toFixed(2);
    const isPositive = value > 0;
    return {
      text: text,
      color: isPositive ? 'green' : 'red',
      arrow: isPositive ? '▲' : '▼'
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset the submitting map when apiRcaLoading changes from true to false
    if (changes['apiRcaLoading'] && !changes['apiRcaLoading'].firstChange) {
      if (changes['apiRcaLoading'].previousValue === true && changes['apiRcaLoading'].currentValue === false) {
        // API completed, reset submitting state and mark as submitted
        this.rcaSubmittingMap = {};
      }
    }
  }
}
